
import { UseFormRegister, FieldErrors, UseFormWatch } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface PersonalInfoData {
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  birth_date: string;
  gender: string;
  marital_status: string;
  nationality: string;
  has_children: boolean;
  kinderzulagen_eligible: boolean;
}

interface PersonalInfoSectionProps {
  register: UseFormRegister<any>;
  errors: FieldErrors;
  setValue: (name: string, value: any) => void;
  watch: UseFormWatch<any>;
}

export function PersonalInfoSection({ register, errors, setValue, watch }: PersonalInfoSectionProps) {
  const nationality = watch("nationality");

  return (
    <Card>
      <CardHeader>
        <CardTitle>Persönliche Informationen</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Vorname</Label>
            <Input 
              {...register("firstname", { required: "Vorname ist erforderlich" })} 
              placeholder="Max"
            />
            {errors.firstname && <p className="text-red-500 text-sm">{errors.firstname.message as string}</p>}
          </div>
          
          <div className="space-y-2">
            <Label>Nachname</Label>
            <Input 
              {...register("lastname", { required: "Nachname ist erforderlich" })} 
              placeholder="Mustermann"
            />
            {errors.lastname && <p className="text-red-500 text-sm">{errors.lastname.message as string}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>E-Mail</Label>
            <Input 
              type="email"
              {...register("email", { 
                required: "E-Mail ist erforderlich",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Ungültige E-Mail-Adresse"
                }
              })} 
              placeholder="max.mustermann@example.com"
            />
            {errors.email && <p className="text-red-500 text-sm">{errors.email.message as string}</p>}
          </div>
          
          <div className="space-y-2">
            <Label>Telefon</Label>
            <Input 
              {...register("phone")} 
              placeholder="+41 79 123 45 67"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Geburtsdatum</Label>
            <Input 
              type="date"
              {...register("birth_date", { required: "Geburtsdatum ist erforderlich" })} 
            />
            {errors.birth_date && <p className="text-red-500 text-sm">{errors.birth_date.message as string}</p>}
          </div>
          
          <div className="space-y-2">
            <Label>Geschlecht</Label>
            <Select onValueChange={(value) => setValue("gender", value)}>
              <SelectTrigger>
                <SelectValue placeholder="Wählen..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Männlich</SelectItem>
                <SelectItem value="female">Weiblich</SelectItem>
                <SelectItem value="other">Divers</SelectItem>
                <SelectItem value="prefer_not_to_say">Keine Angabe</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label>Zivilstand</Label>
            <Select onValueChange={(value) => setValue("marital_status", value)}>
              <SelectTrigger>
                <SelectValue placeholder="Wählen..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="single">Ledig</SelectItem>
                <SelectItem value="married">Verheiratet</SelectItem>
                <SelectItem value="divorced">Geschieden</SelectItem>
                <SelectItem value="widowed">Verwitwet</SelectItem>
                <SelectItem value="registered_partnership">Eingetragene Partnerschaft</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Nationalität</Label>
            <Select onValueChange={(value) => setValue("nationality", value)} defaultValue="Swiss">
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Swiss">Schweiz</SelectItem>
                <SelectItem value="German">Deutschland</SelectItem>
                <SelectItem value="Austrian">Österreich</SelectItem>
                <SelectItem value="French">Frankreich</SelectItem>
                <SelectItem value="Italian">Italien</SelectItem>
                <SelectItem value="Other EU">Andere EU</SelectItem>
                <SelectItem value="Other">Andere</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <input 
                type="checkbox" 
                {...register("has_children")} 
                onChange={(e) => {
                  setValue("has_children", e.target.checked);
                  setValue("kinderzulagen_eligible", e.target.checked);
                }}
              />
              Hat Kinder (Kinderzulagen berechtigt)
            </Label>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
